import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useTheme } from '../../../hooks/useTheme';
import Card3D from '../../../components/base/Card3D';
import ParticleBackground from '../../../components/base/ParticleBackground';
import AnimatedCounter from '../../../components/base/AnimatedCounter';

export default function TechnicalArchitecture() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: mainRef, isVisible: mainVisible } = useScrollAnimation();
  const { ref: metricsRef, isVisible: metricsVisible } = useScrollAnimation();

  const layers = [
    {
      name: t('core.modular.consensus'),
      desc: t('core.modular.consensus.desc'),
      icon: 'ri-shield-check-line',
      gradient: 'from-[#E84A1F] to-[#F06030]'
    },
    {
      name: t('core.modular.execution'),
      desc: t('core.modular.execution.desc'),
      icon: 'ri-flashlight-line',
      gradient: 'from-[#F06030] to-[#E84A1F]'
    },
    {
      name: t('core.modular.data'),
      desc: t('core.modular.data.desc'),
      icon: 'ri-database-2-line',
      gradient: 'from-[#C83A15] to-[#E84A1F]'
    }
  ];

  const metrics = [
    { value: '10,000+', labelKey: 'tech.tps', icon: 'ri-speed-line', suffix: 'TPS' },
    { value: '100+', labelKey: 'tech.nodes', icon: 'ri-server-line', suffix: '' },
    { value: '24/7', labelKey: 'tech.uptime.label', icon: 'ri-time-line', suffix: '' },
    { value: 'EVM', labelKey: 'tech.evm', icon: 'ri-code-box-line', suffix: '' }
  ];

  return (
    <section id="architecture" className={`py-16 md:py-24 lg:py-28 relative overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#0d1117]' : 'bg-white'}`}>
      {/* 背景装饰 */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/4 left-1/4 w-[250px] md:w-[500px] h-[250px] md:h-[500px] ${isDark ? 'bg-[#E84A1F]/5' : 'bg-[#E84A1F]/3'} rounded-full blur-[120px]`}></div>
        <div className={`absolute bottom-1/4 right-1/4 w-[200px] md:w-[400px] h-[200px] md:h-[400px] ${isDark ? 'bg-[#F06030]/5' : 'bg-[#F06030]/3'} rounded-full blur-[100px]`}></div>
        
        {/* 顶部渐变光带 */}
        <div className={`absolute top-0 left-0 right-0 h-[300px] md:h-[500px] bg-gradient-to-b ${isDark ? 'from-[#E84A1F]/[0.03]' : 'from-[#E84A1F]/[0.02]'} via-transparent to-transparent`}></div>
        
        {/* 底部渐变 */}
        <div className={`absolute bottom-0 left-0 right-0 h-[200px] md:h-[300px] bg-gradient-to-t ${isDark ? 'from-[#161b22]/50' : 'from-[#f8fafb]/50'} to-transparent`}></div>
        
        {/* 网格背景 */}
        <div className={`absolute inset-0 ${isDark ? 'opacity-[0.05]' : 'opacity-[0.03]'}`} style={{
          backgroundImage: `linear-gradient(${isDark ? 'rgba(255,255,255,0.3)' : 'rgba(13,17,23,0.3)'} 1px, transparent 1px), linear-gradient(90deg, ${isDark ? 'rgba(255,255,255,0.3)' : 'rgba(13,17,23,0.3)'} 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>

        {/* 点阵图案 */}
        <div className={`absolute inset-0 ${isDark ? 'opacity-[0.03]' : 'opacity-[0.02]'}`} style={{
          backgroundImage: 'radial-gradient(circle, #E84A1F 1px, transparent 1px)',
          backgroundSize: '35px 35px'
        }}></div>

        {/* 装饰性几何图形 */}
        <div className={`absolute top-[15%] left-[8%] w-[80px] md:w-[150px] h-[80px] md:h-[150px] border ${isDark ? 'border-[#E84A1F]/10' : 'border-[#E84A1F]/5'} rounded-2xl rotate-12 hidden md:block`}></div>
        <div className={`absolute bottom-[20%] right-[5%] w-[100px] md:w-[180px] h-[100px] md:h-[180px] border border-dashed ${isDark ? 'border-[#F06030]/10' : 'border-[#F06030]/5'} rounded-full hidden md:block`}></div>
        <div className={`absolute top-[50%] right-[3%] w-[60px] md:w-[100px] h-[60px] md:h-[100px] border ${isDark ? 'border-[#E84A1F]/10' : 'border-[#E84A1F]/5'} rounded-xl -rotate-6 hidden lg:block`}></div>

        {/* 渐变斜线 */}
        <div className={`absolute top-0 right-0 w-1/3 h-full ${isDark ? 'opacity-[0.02]' : 'opacity-[0.015]'} hidden lg:block`} style={{
          backgroundImage: 'repeating-linear-gradient(-45deg, #E84A1F 0, #E84A1F 1px, transparent 0, transparent 50%)',
          backgroundSize: '25px 25px'
        }}></div>

        {/* 浮动圆点 */}
        <div className={`absolute top-[10%] right-[20%] w-3 h-3 ${isDark ? 'bg-[#E84A1F]/20' : 'bg-[#E84A1F]/10'} rounded-full hidden md:block`}></div>
        <div className={`absolute top-[30%] left-[10%] w-2 h-2 ${isDark ? 'bg-[#F06030]/20' : 'bg-[#F06030]/10'} rounded-full hidden md:block`}></div>
        <div className={`absolute bottom-[15%] left-[20%] w-4 h-4 ${isDark ? 'bg-[#E84A1F]/10' : 'bg-[#E84A1F]/5'} rounded-full hidden md:block`}></div>

        {/* 粒子背景效果 */}
        <ParticleBackground 
          particleCount={70} 
          speed={0.35} 
          connectDistance={110}
        />
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* 标题区域 */}
        <div 
          ref={headerRef}
          className={`mb-10 md:mb-16 relative transition-all duration-1000 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* 装饰性大数字 */}
          <div className={`absolute -top-8 -left-4 text-[80px] md:text-[140px] font-bold ${isDark ? 'text-white/[0.02]' : 'text-[#0d1117]/[0.02]'} leading-none select-none pointer-events-none hidden md:block`}>
            02
          </div>

          <div className="flex flex-col md:flex-row items-start gap-4 md:gap-12">
            {/* 左侧编号栏 */}
            <div className="flex md:flex-col items-center gap-3 pt-0 md:pt-2">
              <div className="w-9 md:w-10 h-9 md:h-10 rounded-full bg-gradient-to-br from-[#E84A1F] to-[#F06030] flex items-center justify-center text-white font-bold text-sm">
                02
              </div>
              <div className={`hidden md:block w-px bg-gradient-to-b from-[#E84A1F] to-transparent mt-3 transition-all duration-1000 delay-300 ${
                headerVisible ? 'h-20' : 'h-0'
              }`}></div>
            </div>

            {/* 标题内容 */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-3 md:mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#E84A1F] rounded-full animate-pulse"></span>
                  <span className={`text-xs uppercase tracking-widest hidden sm:inline ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>Modular Design</span>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-start justify-between gap-4 md:gap-0">
                <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold transition-colors duration-500 ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>
                  {t('tech.title')}
                </h2>
                <div className={`max-w-full md:max-w-[400px] pl-0 md:pl-8 border-l-0 md:border-l transition-all duration-1000 delay-500 ${isDark ? 'border-white/10' : 'border-[#0d1117]/10'} ${
                  headerVisible ? 'opacity-100 translate-x-0' : 'opacity-0 md:translate-x-10'
                }`}>
                  <p className={`text-xs md:text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-[#0d1117]/50'}`}>
                    {t('tech.desc1')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 主内容区域 */}
        <div 
          ref={mainRef}
          className={`backdrop-blur-sm border rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-10 relative overflow-hidden transition-all duration-1000 shadow-xl ${
            isDark 
              ? 'bg-[#161b22] border-white/5 shadow-black/20' 
              : 'bg-[#f8fafb] border-[#0d1117]/5 shadow-[#0d1117]/5'
          } ${mainVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}
        >
          {/* 卡片内装饰 */}
          <div className={`absolute top-0 right-0 w-[150px] md:w-[300px] h-[150px] md:h-[300px] ${isDark ? 'bg-[#E84A1F]/5' : 'bg-[#E84A1F]/3'} rounded-full blur-[80px]`}></div>
          <div className={`absolute bottom-0 left-0 w-[100px] md:w-[200px] h-[100px] md:h-[200px] ${isDark ? 'bg-[#F06030]/5' : 'bg-[#F06030]/3'} rounded-full blur-[60px]`}></div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-8 relative z-10">
            {/* 左侧 - 架构图 */}
            <div className={`lg:col-span-5 transition-all duration-700 ${
              mainVisible ? 'opacity-100 translate-x-0' : 'opacity-0 lg:-translate-x-10'
            }`}>
              <Card3D intensity={10} scale={1.02}>
                <div className={`border rounded-xl md:rounded-2xl p-4 md:p-6 relative group hover:border-[#E84A1F]/30 transition-all duration-500 shadow-lg ${
                  isDark 
                    ? 'bg-[#0d1117] border-white/10 shadow-black/20' 
                    : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                }`}>
                  {/* 角标装饰 */}
                  <div className={`absolute -top-3 -right-3 w-6 h-6 border-t-2 border-r-2 border-[#E84A1F]/30 rounded-tr-lg hidden md:block`}></div>
                  <div className={`absolute -bottom-3 -left-3 w-6 h-6 border-b-2 border-l-2 border-[#E84A1F]/30 rounded-bl-lg hidden md:block`}></div>

                  {/* 标题 */}
                  <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                    <div className="w-7 md:w-8 h-7 md:h-8 bg-gradient-to-br from-[#E84A1F] to-[#F06030] rounded-lg flex items-center justify-center">
                      <i className="ri-stack-line text-white text-xs md:text-sm"></i>
                    </div>
                    <span className={`text-xs md:text-sm font-semibold ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>{t('tech.layered.title')}</span>
                    <span className={`text-xs ml-auto ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>3 {t('tech.layered.layers')}</span>
                  </div>

                  {/* 架构层级 */}
                  <div className="space-y-2 md:space-y-3">
                    {layers.map((layer, index) => (
                      <div key={index} className={`transition-all duration-500 ${
                        mainVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                      }`} style={{ transitionDelay: `${300 + index * 150}ms` }}>
                        <div className={`bg-gradient-to-r ${layer.gradient} rounded-lg md:rounded-xl p-3 md:p-4 relative overflow-hidden group/layer hover:scale-[1.02] transition-transform duration-300 cursor-pointer`}>
                          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover/layer:translate-x-[100%] transition-transform duration-700"></div>
                          
                          <div className="flex items-center justify-between relative z-10">
                            <div>
                              <h4 className="text-xs md:text-sm font-semibold text-white mb-0.5 md:mb-1">{layer.name}</h4>
                              <p className="text-[10px] md:text-xs text-white/70">{layer.desc}</p>
                            </div>
                            <div className="w-8 md:w-9 h-8 md:h-9 bg-white/20 rounded-lg flex items-center justify-center">
                              <i className={`${layer.icon} text-base md:text-lg text-white`}></i>
                            </div>
                          </div>
                        </div>

                        {/* 连接线 */}
                        {index < layers.length - 1 && (
                          <div className="flex justify-center py-1.5 md:py-2">
                            <div className="flex flex-col items-center">
                              <div className="w-px h-2 md:h-3 bg-gradient-to-b from-[#E84A1F]/50 to-[#E84A1F]/20"></div>
                              <div className={`w-4 md:w-5 h-4 md:h-5 border border-[#E84A1F]/30 rounded-full flex items-center justify-center ${isDark ? 'bg-[#0d1117]' : 'bg-white'}`}>
                                <i className="ri-arrow-down-s-line text-[#E84A1F] text-xs md:text-sm"></i>
                              </div>
                              <div className="w-px h-2 md:h-3 bg-gradient-to-b from-[#E84A1F]/20 to-[#E84A1F]/50"></div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* 底部标签 */}
                  <div className={`flex flex-wrap items-center gap-1.5 md:gap-2 mt-4 md:mt-5 pt-3 md:pt-4 border-t ${isDark ? 'border-white/10' : 'border-[#0d1117]/10'}`}>
                    {[t('tech.layered.tag1'), t('tech.layered.tag2'), t('tech.layered.tag3')].map((tag, i) => (
                      <span key={i} className={`px-2 md:px-2.5 py-0.5 md:py-1 rounded-md text-[10px] md:text-xs ${isDark ? 'bg-white/5 text-gray-400' : 'bg-[#0d1117]/5 text-[#0d1117]/50'}`}>{tag}</span>
                    ))}
                  </div>
                </div>
              </Card3D>
            </div>

            {/* 右侧 - 数据指标 */}
            <div className={`lg:col-span-7 flex flex-col gap-4 md:gap-5 transition-all duration-700 delay-200 ${
              mainVisible ? 'opacity-100 translate-x-0' : 'opacity-0 lg:translate-x-10'
            }`}>
              {/* 大数据展示 */}
              <div className="grid grid-cols-2 gap-3 md:gap-5">
                {/* 99.9% */}
                <Card3D intensity={12} scale={1.03}>
                  <div className={`border rounded-xl md:rounded-2xl p-4 md:p-5 relative group hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden shadow-lg ${
                    isDark 
                      ? 'bg-[#0d1117] border-white/10 shadow-black/20' 
                      : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                  }`}>
                    <div className={`absolute top-0 right-0 w-20 md:w-24 h-20 md:h-24 ${isDark ? 'bg-[#E84A1F]/10' : 'bg-[#E84A1F]/5'} rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity`}></div>
                    
                    <div className="flex items-start justify-between mb-2 md:mb-3">
                      <div className="w-8 md:w-9 h-8 md:h-9 bg-gradient-to-br from-[#E84A1F]/20 to-[#F06030]/20 rounded-lg flex items-center justify-center border border-[#E84A1F]/20 group-hover:scale-110 group-hover:rotate-6 transition-all">
                        <i className="ri-shield-check-line text-[#E84A1F] text-sm md:text-base"></i>
                      </div>
                      <span className="text-[10px] md:text-xs text-[#E84A1F]/60 uppercase tracking-wider">Uptime</span>
                    </div>
                    
                    <div className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-0.5 md:mb-1 relative ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>
                      <AnimatedCounter end={99.9} decimals={1} duration={2500} />
                      <span className="text-lg sm:text-xl md:text-2xl text-[#E84A1F]">%</span>
                    </div>
                    <div className={`text-xs md:text-sm mb-0.5 ${isDark ? 'text-gray-400' : 'text-[#0d1117]/60'}`}>{t('tech.uptime')}</div>
                    <div className={`text-[10px] md:text-xs mb-2 md:mb-3 hidden sm:block ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>{t('tech.uptime.desc')}</div>

                    {/* 进度条装饰 */}
                    <div className={`h-1 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-[#0d1117]/5'}`}>
                      <div className="h-full w-[99.9%] bg-gradient-to-r from-[#E84A1F] to-[#F06030] rounded-full relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-[shimmer_2s_infinite]"></div>
                      </div>
                    </div>
                  </div>
                </Card3D>

                {/* <2s */}
                <Card3D intensity={12} scale={1.03}>
                  <div className={`border rounded-xl md:rounded-2xl p-4 md:p-5 relative group hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden shadow-lg ${
                    isDark 
                      ? 'bg-[#0d1117] border-white/10 shadow-black/20' 
                      : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                  }`}>
                    <div className={`absolute top-0 right-0 w-20 md:w-24 h-20 md:h-24 ${isDark ? 'bg-[#F06030]/10' : 'bg-[#F06030]/5'} rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity`}></div>
                    
                    <div className="flex items-start justify-between mb-2 md:mb-3">
                      <div className="w-8 md:w-9 h-8 md:h-9 bg-gradient-to-br from-[#E84A1F]/20 to-[#F06030]/20 rounded-lg flex items-center justify-center border border-[#E84A1F]/20">
                        <i className="ri-timer-flash-line text-[#E84A1F] text-sm md:text-base"></i>
                      </div>
                      <span className="text-[10px] md:text-xs text-[#E84A1F]/60 uppercase tracking-wider">Finality</span>
                    </div>
                    
                    <div className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-0.5 md:mb-1 ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>
                      <span className="text-[#E84A1F]">&lt;</span>2
                      <span className={`text-lg sm:text-xl md:text-2xl ${isDark ? 'text-gray-500' : 'text-[#0d1117]/60'}`}>s</span>
                    </div>
                    <div className={`text-xs md:text-sm mb-0.5 ${isDark ? 'text-gray-400' : 'text-[#0d1117]/60'}`}>{t('tech.finality')}</div>
                    <div className={`text-[10px] md:text-xs mb-2 md:mb-3 hidden sm:block ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>{t('tech.finality.desc')}</div>

                    {/* 脉冲动画 */}
                    <div className="flex items-end gap-1 h-4 md:h-5">
                      {[...Array(8)].map((_, i) => (
                        <div 
                          key={i} 
                          className="flex-1 bg-gradient-to-t from-[#E84A1F] to-[#F06030] rounded-sm"
                          style={{ 
                            height: `${30 + Math.random() * 70}%`,
                            animation: `pulse ${1 + Math.random()}s ease-in-out infinite alternate`,
                            animationDelay: `${i * 0.1}s`
                          }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </Card3D>
              </div>

              {/* 技术特性横向卡片 */}
              <div className="grid grid-cols-3 gap-2 md:gap-4">
                <Card3D intensity={15} scale={1.05}>
                  <div className={`border rounded-lg md:rounded-xl p-3 md:p-4 group hover:border-[#E84A1F]/20 transition-all cursor-pointer shadow-md ${
                    isDark 
                      ? 'bg-[#0d1117] border-white/10 shadow-black/20' 
                      : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                  }`}>
                    <div className="flex items-center gap-2 md:gap-3 mb-1.5 md:mb-2">
                      <div className="w-6 md:w-8 h-6 md:h-8 bg-gradient-to-br from-[#E84A1F]/20 to-[#F06030]/20 rounded-lg flex items-center justify-center">
                        <i className="ri-cpu-line text-[#E84A1F] text-xs md:text-sm"></i>
                      </div>
                      <span className={`text-[10px] md:text-xs hidden sm:inline ${isDark ? 'text-gray-500' : 'text-[#0d1117]/50'}`}>{t('tech.parallel.title')}</span>
                    </div>
                    <div className={`text-sm sm:text-base md:text-xl font-bold group-hover:text-[#E84A1F] transition-colors ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>Multi-Thread</div>
                    <div className={`text-[10px] md:text-xs mt-0.5 md:mt-1 hidden sm:block ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>{t('tech.parallel.desc')}</div>
                  </div>
                </Card3D>

                <Card3D intensity={15} scale={1.05}>
                  <div className={`border rounded-lg md:rounded-xl p-3 md:p-4 group hover:border-[#E84A1F]/20 transition-all cursor-pointer shadow-md ${
                    isDark 
                      ? 'bg-[#0d1117] border-white/10 shadow-black/20' 
                      : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                  }`}>
                    <div className="flex items-center gap-2 md:gap-3 mb-1.5 md:mb-2">
                      <div className="w-6 md:w-8 h-6 md:h-8 bg-gradient-to-br from-[#E84A1F]/20 to-[#F06030]/20 rounded-lg flex items-center justify-center">
                        <i className="ri-database-2-line text-[#E84A1F] text-xs md:text-sm"></i>
                      </div>
                      <span className={`text-[10px] md:text-xs hidden sm:inline ${isDark ? 'text-gray-500' : 'text-[#0d1117]/50'}`}>{t('tech.optimized.title')}</span>
                    </div>
                    <div className={`text-sm sm:text-base md:text-xl font-bold group-hover:text-[#E84A1F] transition-colors ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>Optimized</div>
                    <div className={`text-[10px] md:text-xs mt-0.5 md:mt-1 hidden sm:block ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>{t('tech.optimized.desc')}</div>
                  </div>
                </Card3D>

                <Card3D intensity={15} scale={1.05}>
                  <div className={`border rounded-lg md:rounded-xl p-3 md:p-4 group hover:border-[#E84A1F]/20 transition-all cursor-pointer shadow-md ${
                    isDark 
                      ? 'bg-[#0d1117] border-white/10 shadow-black/20' 
                      : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                  }`}>
                    <div className="flex items-center gap-2 md:gap-3 mb-1.5 md:mb-2">
                      <div className="w-6 md:w-8 h-6 md:h-8 bg-gradient-to-br from-[#E84A1F]/20 to-[#F06030]/20 rounded-lg flex items-center justify-center">
                        <i className="ri-lock-line text-[#E84A1F] text-xs md:text-sm"></i>
                      </div>
                      <span className={`text-[10px] md:text-xs hidden sm:inline ${isDark ? 'text-gray-500' : 'text-[#0d1117]/50'}`}>{t('tech.zerotrust.title')}</span>
                    </div>
                    <div className={`text-sm sm:text-base md:text-xl font-bold group-hover:text-[#E84A1F] transition-colors ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>Zero-Trust</div>
                    <div className={`text-[10px] md:text-xs mt-0.5 md:mt-1 hidden sm:block ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>{t('tech.zerotrust.desc')}</div>
                  </div>
                </Card3D>
              </div>

              {/* CTA 按钮区域 */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-gradient-to-r from-[#E84A1F]/10 to-transparent rounded-lg md:rounded-xl p-4 md:p-5 border border-[#E84A1F]/10 gap-3 sm:gap-0">
                <div>
                  <div className={`text-xs md:text-sm mb-0.5 ${isDark ? 'text-gray-300' : 'text-[#0d1117]/70'}`}>{t('tech.explore.title')}</div>
                  <div className={`text-[10px] md:text-xs ${isDark ? 'text-gray-500' : 'text-[#0d1117]/50'}`}>{t('tech.explore.desc')}</div>
                </div>
                <button className="group relative px-4 md:px-6 py-2.5 md:py-3 bg-gradient-to-r from-[#E84A1F] to-[#F06030] rounded-lg md:rounded-xl text-xs md:text-sm font-semibold text-white overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_rgba(232,74,31,0.4)] hover:scale-105 active:scale-95 whitespace-nowrap cursor-pointer w-full sm:w-auto">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {t('tech.cta')}
                    <div className="w-5 md:w-6 h-5 md:h-6 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
                      <i className="ri-arrow-right-line text-white text-xs md:text-sm group-hover:translate-x-0.5 transition-transform"></i>
                    </div>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 底部性能指标 */}
        <div 
          ref={metricsRef}
          className="mt-6 md:mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4"
        >
          {metrics.map((metric, index) => (
            <Card3D key={index} intensity={15} scale={1.04}>
              <div 
                className={`border rounded-xl md:rounded-2xl p-4 md:p-5 text-center relative group hover:border-[#E84A1F]/20 transition-all duration-500 cursor-pointer shadow-lg ${
                  isDark 
                    ? 'bg-[#161b22] border-white/10 shadow-black/20' 
                    : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                } ${metricsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* 悬停光效 */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#E84A1F]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl md:rounded-2xl"></div>
                
                <div className="relative z-10">
                  <div className="w-9 md:w-11 h-9 md:h-11 bg-gradient-to-br from-[#E84A1F]/10 to-[#F06030]/10 border border-[#E84A1F]/20 rounded-lg md:rounded-xl flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                    <i className={`${metric.icon} text-lg md:text-xl text-[#E84A1F]`}></i>
                  </div>
                  <div className={`text-lg md:text-2xl font-bold mb-0.5 md:mb-1 group-hover:text-[#E84A1F] transition-colors ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>
                    {index === 0 ? <AnimatedCounter end={10000} suffix="+" duration={2000} /> : metric.value}
                  </div>
                  <div className={`text-[10px] md:text-xs ${isDark ? 'text-gray-500' : 'text-[#0d1117]/50'}`}>{t(metric.labelKey)}</div>
                </div>

                {/* 序号 */}
                <div className={`absolute top-2 md:top-3 right-2 md:right-3 text-[10px] md:text-xs font-mono ${isDark ? 'text-white/10' : 'text-[#0d1117]/10'}`}>0{index + 1}</div>
              </div>
            </Card3D>
          ))}
        </div>

        {/* 底部连接线装饰 */}
        <div className={`hidden md:flex items-center justify-center mt-8 gap-2`}>
          <div className={`w-20 h-px bg-gradient-to-r from-transparent ${isDark ? 'to-white/10' : 'to-[#0d1117]/10'}`}></div>
          <div className="w-2 h-2 rounded-full bg-[#E84A1F]/30"></div>
          <div className="w-2 h-2 rounded-full bg-[#E84A1F]/50"></div>
          <div className="w-2 h-2 rounded-full bg-[#E84A1F]/30"></div>
          <div className={`w-20 h-px bg-gradient-to-l from-transparent ${isDark ? 'to-white/10' : 'to-[#0d1117]/10'}`}></div>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </section>
  );
}
